/**
 * ORDENAÇÃO, RECORTE e RESUMO da lista do catálogo.
 *
 * Vive fora da página pelo mesmo motivo do `quoteListFilters`/`clientsListFilters`.
 * O que este tem de próprio é a **hierarquia**: variação É uma linha filha de
 * `products` (#30), então toda regra aqui recebe o GRUPO (raiz + filhas) e não
 * a linha solta.
 *
 * ─── O QUE QUEBRA SE FOR ESCRITO NA PÁGINA ──────────────────────────────────
 *
 * · **Preço de pai não é `parent.price`.** O pai não tem preço único: quem
 *   vende é a variação. Ordenar ou recortar por `parent.price` compararia uma
 *   Camiseta de R$ 40–45 pelo número que estiver na coluna do agrupador — que
 *   deveria ser o MENOR das filhas, mas é justamente o que uma linha vinda do
 *   cache antigo pode ter desatualizado. A faixa sai de `priceRange(variants)`.
 * · **Saldo de pai é a SOMA das filhas** (ele não tem saldo próprio, #30b).
 *   Recortar "sem estoque" por `parent.stock_quantity` marcaria como zerado um
 *   produto com 40 unidades espalhadas em P/M/G.
 * · **Ausência não é zero.** `min_stock` nulo é "sem alerta", não "mínimo
 *   zero" (#52); um produto sem rastreio não é "estoque zerado", ele está fora
 *   da conta — e por isso `tracked`/`out` só olham quem rastreia.
 * · **Ordem instável** (#93): todo comparador desempata por `id`, senão dois
 *   itens do mesmo preço trocam de lugar sozinhos entre renders.
 */
import type { Product } from '@/types/database';
import { isLowStock, isTrackedProduct } from '@/lib/stock';
import { hasTrackedVariant, priceRange, totalVariantStock } from '@/lib/variants';
import { normalizeSearchText } from '@/lib/quoteSearch';
import { matchesInternalCode } from '@/lib/productCodes';
import { quoteTotalNumber } from '@/lib/quotesListSummary';
import { matchesListRange, type ListValueRange } from '@/lib/listFilters';

/** O que a lista lê de um item (o cache devolve linhas parciais). */
export type CatalogRow = Partial<Product> & { id: string; name: string; price: number };

/** Uma raiz com as filhas dela anexadas — a unidade que a lista renderiza. */
export interface CatalogGroupLike<T extends CatalogRow = CatalogRow> {
  parent: T;
  variants: T[];
}

// ─── Busca ───────────────────────────────────────────────────────────────────

/**
 * Termo vazio casa com tudo.
 *
 * A raiz aparece se ela própria casar OU se alguma variação dela casar — senão
 * procurar por "P" (ou pelo código de barras que está na etiqueta da filha) não
 * acharia nada, e a lista pareceria dizer que o produto não existe. É a mesma
 * regra que a página já aplicava; o que ela ganhou ao virar módulo foi o
 * **acento** (#190d): o catálogo é escrito à mão em pt-BR, e um
 * `toLowerCase().includes()` cru devolvia ZERO para quem digitava "instalacao"
 * no teclado do celular.
 *
 * O CÓDIGO DE BARRAS não entra por texto solto: ele passa pelo mesmo casamento
 * de dígitos, porque quem digita "789 2222" espera achar "7892222222222".
 */
export function catalogGroupMatchesSearch(group: CatalogGroupLike, term: string): boolean {
  const needle = normalizeSearchText(term);
  if (!needle) return true;

  const matchesRow = (row: CatalogRow, isChild: boolean): boolean => {
    if (!isChild && normalizeSearchText(row.name).includes(needle)) return true;
    if (isChild && normalizeSearchText(row.variant_name).includes(needle)) return true;
    if (!isChild && normalizeSearchText(row.category).includes(needle)) return true;
    // Código interno entra na busca TEXTUAL (não no scanner: o scanner lê GTIN,
    // e um SKU interno não está impresso em código de barras).
    if (matchesInternalCode(row, term)) return true;
    return normalizeSearchText(row.barcode).includes(needle);
  };

  if (matchesRow(group.parent, false)) return true;
  return group.variants.some((variant) => matchesRow(variant, true));
}

// ─── Preço e saldo do GRUPO ─────────────────────────────────────────────────

/** Menor preço vendável do grupo (o do próprio item, quando não há variação). */
export function groupMinPrice(group: CatalogGroupLike): number {
  const range = group.variants.length > 0 ? priceRange(group.variants) : null;
  return range ? range.min : quoteTotalNumber(group.parent.price);
}

/** `true` se o grupo tem QUALQUER preço vendável dentro da faixa. */
export function matchesPriceRange(group: CatalogGroupLike, range: ListValueRange): boolean {
  if (group.variants.length === 0) return matchesListRange(group.parent.price, range);
  // Basta UMA variação caber: a Camiseta continua sendo um resultado válido de
  // "até R$ 42" mesmo que a G custe 45 — esconder o card inteiro faria a busca
  // parecer que o produto não existe.
  return group.variants.some((variant) => matchesListRange(variant.price, range));
}

// ─── Ordenação ───────────────────────────────────────────────────────────────

export type CatalogSortOption = 'name' | 'nameDesc' | 'highest' | 'lowest' | 'recent';

export const CATALOG_SORT_LABELS: Record<CatalogSortOption, string> = {
  name: 'Nome (A–Z)',
  nameDesc: 'Nome (Z–A)',
  highest: 'Maior preço',
  lowest: 'Menor preço',
  recent: 'Cadastro mais recente',
};

export const CATALOG_SORT_ORDER: readonly CatalogSortOption[] = [
  'name',
  'nameDesc',
  'highest',
  'lowest',
  'recent',
];

/** A–Z é o padrão: é como a tela sempre se comportou (`.order('name')`). */
export const DEFAULT_CATALOG_SORT: CatalogSortOption = 'name';

function textOf(value: unknown): string {
  return normalizeSearchText(value) || '￿';
}

function timeOf(row: CatalogRow): number {
  const parsed = new Date(row.created_at ?? '').getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

export function sortCatalogGroups<T extends CatalogRow>(
  groups: readonly CatalogGroupLike<T>[],
  option: CatalogSortOption,
): CatalogGroupLike<T>[] {
  const rows = [...groups];
  const byId = (a: CatalogGroupLike<T>, b: CatalogGroupLike<T>) =>
    String(a.parent.id).localeCompare(String(b.parent.id));

  switch (option) {
    case 'nameDesc':
      return rows.sort(
        (a, b) => textOf(b.parent.name).localeCompare(textOf(a.parent.name)) || byId(a, b),
      );
    case 'highest':
      return rows.sort((a, b) => groupMinPrice(b) - groupMinPrice(a) || byId(a, b));
    case 'lowest':
      return rows.sort((a, b) => groupMinPrice(a) - groupMinPrice(b) || byId(a, b));
    case 'recent':
      return rows.sort((a, b) => timeOf(b.parent) - timeOf(a.parent) || byId(a, b));
    case 'name':
    default:
      return rows.sort(
        (a, b) => textOf(a.parent.name).localeCompare(textOf(b.parent.name)) || byId(a, b),
      );
  }
}

// ─── Recorte por ESTOQUE ────────────────────────────────────────────────────

export type CatalogStockFilter = 'all' | 'tracked' | 'low' | 'out';

export const CATALOG_STOCK_FILTERS: readonly { value: CatalogStockFilter; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'tracked', label: 'Com saldo' },
  { value: 'low', label: 'Estoque baixo' },
  { value: 'out', label: 'Sem saldo' },
];

/**
 * A linha do cache é PARCIAL, e `isTrackedProduct` exige as duas colunas —
 * então o estreitamento acontece num lugar só, em vez de a regra de "o que
 * rastreia estoque" ser reescrita aqui (ela é do `stock.ts`, e uma segunda
 * cópia dela divergiria na primeira mudança).
 */
const tracks = (row: CatalogRow): boolean =>
  isTrackedProduct({ type: row.type, track_stock: row.track_stock } as Pick<
    Product,
    'type' | 'track_stock'
  >);

/** O grupo rastreia estoque? (o pai, pelas filhas). */
export function groupTracksStock(group: CatalogGroupLike): boolean {
  if (group.variants.length > 0) return hasTrackedVariant(group.variants);
  return tracks(group.parent);
}

/** Saldo do grupo — soma das filhas rastreadas quando há variação (#30b). */
export function groupStockBalance(group: CatalogGroupLike): number {
  if (group.variants.length > 0) return totalVariantStock(group.variants);
  return quoteTotalNumber(group.parent.stock_quantity);
}

/** Alerta de estoque baixo — basta UMA variação abaixo do mínimo dela. */
export function groupIsLowStock(group: CatalogGroupLike): boolean {
  if (group.variants.length > 0) {
    return group.variants.some((variant) => tracks(variant) && isLowStock(variant));
  }
  return tracks(group.parent) && isLowStock(group.parent);
}

export function matchesStockFilter(group: CatalogGroupLike, filter: CatalogStockFilter): boolean {
  switch (filter) {
    case 'tracked':
      return groupTracksStock(group) && groupStockBalance(group) > 0;
    case 'low':
      return groupIsLowStock(group);
    case 'out':
      // Só quem RASTREIA pode estar "sem saldo": um serviço (ou um produto que
      // ninguém controla) não tem saldo nenhum, e listá-lo aqui devolveria o
      // catálogo inteiro como se estivesse tudo em falta.
      return groupTracksStock(group) && groupStockBalance(group) <= 0;
    case 'all':
    default:
      return true;
  }
}

/**
 * Quantos filtros do MODAL estão ativos — vira o selo no ícone.
 *
 * A CATEGORIA fica de fora: ela tem os botões visíveis logo acima, e contar um
 * filtro que a tela já mostra faria o selo dizer "1" com o modal em branco.
 */
export function countActiveCatalogFilters(
  stock: CatalogStockFilter,
  range: ListValueRange,
): number {
  let active = 0;
  if (stock !== 'all') active += 1;
  if (range.min !== null || range.max !== null) active += 1;
  return active;
}

// ─── Resumo do hero ─────────────────────────────────────────────────────────

export interface CatalogListSummary {
  /** Itens no recorte — só RAÍZES: uma camiseta P/M/G é UM item, não quatro. */
  count: number;
  /** Quantas variações estão penduradas nesses itens. */
  variantCount: number;
  /** Quantos estão abaixo do mínimo — o número acionável da tela. */
  lowStockCount: number;
}

export function summarizeCatalogList(
  groups: readonly CatalogGroupLike[],
): CatalogListSummary {
  let variantCount = 0;
  let lowStockCount = 0;
  for (const group of groups) {
    variantCount += group.variants.length;
    if (groupIsLowStock(group)) lowStockCount += 1;
  }
  return { count: groups.length, variantCount, lowStockCount };
}

/**
 * O RÓTULO do número do hero. Mesmo raciocínio do `clientListTotalLabel`: o
 * valor ao vivo é a CONTAGEM do recorte, e sem dizer de onde ela saiu o número
 * lê como "é tudo o que eu tenho cadastrado".
 */
export function catalogListTotalLabel(tab: 'service' | 'product', filtered: boolean): string {
  const noun = tab === 'service' ? 'Serviços' : 'Produtos';
  return filtered ? `${noun} no filtro` : `${noun} cadastrados`;
}
